import TimeIcon from "@mui/icons-material/AccessTimeRounded";
import type { SxProps, Theme } from "@mui/material";
import { Alert, AlertTitle, Button, Typography } from "@mui/material";
import { convertISODateShort } from "@entrilia/shared/utilities/dateUtils";

export const CHANGES_WAITING_FOR_APPROVAL_MESSAGE = "These changes are waiting for approval.";

interface PendingChangeRequestInfo {
  referenceCode: string;
  requestedByUserName: string;
  requestedAt: string;
}

interface Props<T extends PendingChangeRequestInfo> {
  pendingChangeRequest: T;
  onViewRequest: (pendingChangeRequest: T) => void;
  showEditingRestrictedNote?: boolean;
  hideIcon?: boolean;
  // A merged request keeps its original creator, who may administer funds this contact cannot see
  hideSubmitter?: boolean;
  alertSx?: SxProps<Theme>;
}

const PendingChangesAlert = <T extends PendingChangeRequestInfo>({
  pendingChangeRequest,
  onViewRequest,
  showEditingRestrictedNote,
  hideIcon,
  hideSubmitter,
  alertSx,
}: Props<T>) => {
  return (
    <Alert
      severity="warning"
      icon={hideIcon ? false : undefined}
      iconMapping={{ warning: <TimeIcon fontSize="inherit" /> }}
      sx={{ position: "relative", ...alertSx }}
    >
      <AlertTitle>Pending Changes (#{pendingChangeRequest.referenceCode})</AlertTitle>
      <Button
        variant="outlined"
        color="inherit"
        sx={{ position: "absolute", right: 12, top: 12, py: 0 }}
        onClick={() => onViewRequest(pendingChangeRequest)}
      >
        View Request
      </Button>
      <Typography sx={{ pt: 0.5 }}>
        {hideSubmitter ? (
          CHANGES_WAITING_FOR_APPROVAL_MESSAGE
        ) : (
          <>
            Change request submitted by <strong>{pendingChangeRequest.requestedByUserName}</strong> on{" "}
            <strong>{convertISODateShort(pendingChangeRequest.requestedAt)}</strong> is waiting for approval.
          </>
        )}
        {showEditingRestrictedNote ? " Editing will be restricted until the request is approved." : ""}
      </Typography>
    </Alert>
  );
};

export default PendingChangesAlert;
